import type {
  CampusEvent,
  EventFilters,
  EventSource,
} from "@/app/types/events";

export const ALL_EVENT_SOURCES: EventSource[] = [
  "getinvolved",
  "athletics",
  "recreation",
  "mason_gross",
  "newbrunswick",
  "imleagues",
];
export const DATE_RANGE_OPTIONS: {
  value: EventFilters["dateRange"];
  label: string;
}[] = [
  { value: "all", label: "All upcoming" },
  { value: "today", label: "Today" },
  { value: "week", label: "This week" },
  { value: "month", label: "This month" },
];
export type EventTab = "clubs" | "rutgers" | "intramural" | "rooms";
export type SportItem = { label: string; query: string };
export const EVENT_TABS: Array<{ id: "clubs" | "rutgers"; label: string }> = [
  { id: "clubs", label: "Events" },
  { id: "rutgers", label: "Sports" },
];
export const RUTGERS_SPORTS: SportItem[] = [
  "Basketball",
  "Football",
  "Soccer",
  "Baseball",
  "Softball",
  "Lacrosse",
  "Cricket",
  "Volleyball",
  "Wrestling",
  "Swimming",
  "Track",
  "Tennis",
  "Golf",
  "Gymnastics",
  "Rowing",
].map((label) => ({ label, query: label.toLowerCase() }));
export const INTRAMURAL_SPORTS: SportItem[] = [
  "Basketball",
  "Football",
  "Soccer",
  "Softball",
  "Volleyball",
  "Lacrosse",
  "Cricket",
  "Badminton",
  "Tennis",
  "Dodgeball",
].map((label) => ({ label, query: label.toLowerCase() }));
export const RUTGERS_EVENT_SOURCES: EventSource[] = ["athletics"];
export const INTRAMURAL_EVENT_SOURCES: EventSource[] = [
  "recreation",
  "imleagues",
];
export const SPORTS_EVENT_SOURCES: EventSource[] = [
  ...RUTGERS_EVENT_SOURCES,
  ...INTRAMURAL_EVENT_SOURCES,
];
// IMLeagues currently returns facility times without meaningful event names.
// Keep those records available through explicit source selection.
export const DEFAULT_SPORTS_EVENT_SOURCES: EventSource[] = ["athletics", "recreation"];
export const EVENT_SPORTS: SportItem[] = [
  ...RUTGERS_SPORTS,
  ...INTRAMURAL_SPORTS.filter((sport) => !RUTGERS_SPORTS.some((item) => item.query === sport.query)),
];
export const DEFAULT_EVENT_FILTERS: EventFilters = {
  sources: ["getinvolved", "mason_gross", "newbrunswick"],
  dateRange: "all",
  search: "",
  category: null,
};
export const EVENT_DIRECTORY_FIXTURES: Record<
  "populated" | "long",
  CampusEvent[]
> = {
  populated: [
    {
      id: "fixture-activity-fair",
      title: "Fall Student Organization Fair",
      start_time: "2026-09-08T16:00:00-04:00",
      end_time: "2026-09-08T18:00:00-04:00",
      location: "College Avenue Student Center",
      host: "Student Involvement",
      source: "getinvolved",
      source_url: "https://getinvolved.rutgers.edu/",
      category: "Student Life",
      image_url: null,
      is_free: true,
      scraped_at: "2026-09-01T12:00:00Z",
    },
    {
      id: "fixture-game",
      title: "Rutgers Scarlet Knights vs. Visiting Team",
      start_time: "2026-09-12T13:00:00-04:00",
      end_time: null,
      location: "SHI Stadium",
      host: "Rutgers Athletics",
      source: "athletics",
      source_url: "https://scarletknights.com/",
      category: "Football",
      image_url: null,
      is_free: false,
      scraped_at: "2026-09-01T12:00:00Z",
      time_confirmed: true,
    },
    {
      id: "fixture-workshop",
      title:
        "Research and Writing Workshop: Building a Sustainable Academic Practice",
      start_time: "2026-09-10T11:00:00-04:00",
      end_time: "2026-09-10T12:30:00-04:00",
      location: "Alexander Library",
      host: "Rutgers Libraries",
      source: "newbrunswick",
      source_url: "https://www.rutgers.edu/",
      category: "Workshop",
      image_url: null,
      is_free: true,
      scraped_at: "2026-09-01T12:00:00Z",
    },
  ],
  long: Array.from({ length: 12 }, (_, index) => ({
    id: `fixture-long-${index}`,
    title: `Campus event ${index + 1}: Extended example title for a long directory result`,
    start_time: `2026-09-${String((index % 20) + 1).padStart(2, "0")}T16:00:00-04:00`,
    end_time: null,
    location: "College Avenue Campus",
    host: "Rutgers Student Affairs",
    source: "getinvolved" as const,
    source_url: "https://getinvolved.rutgers.edu/",
    category: "Campus life",
    image_url: null,
    is_free: true,
    scraped_at: "2026-09-01T12:00:00Z",
  })),
};
export function eventSummaryLabel(tab: EventTab): string {
  return {
    clubs: "Clubs and campus activity",
    rutgers: "Sports",
    intramural: "Intramural and recreation",
    rooms: "Open study spaces",
  }[tab];
}
export function eventEmptyCopy(tab: EventTab, hasActiveFilters: boolean) {
  const title = hasActiveFilters
    ? `No matching ${tab === "rooms" ? "spaces" : "events"}`
    : tab === "rooms"
      ? "No open study spaces"
      : "No events right now";
  const description = hasActiveFilters
    ? "Try another date range or source."
    : tab === "rooms"
      ? "Try another building or campus."
      : "Try another date range or source.";
  return { title, description };
}
